import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
  HStack,
  Badge,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  Divider,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, ViewIcon, SearchIcon, ChevronDownIcon, SettingsIcon } from '@chakra-ui/icons';
import { useClients, useCreateClient, useUpdateClient, useDeleteClient } from '../hooks/useClients';
import { CreateClientData, UpdateClientData, Client } from '../api/clientApi';
import { PermissionGuard } from './PermissionGuard';
import { Permission } from '../utils/permissions';
import LimitRestrictedButton from './LimitRestrictedButton';
import ClientForm from './Forms/ClientForm';
import ClientViewModal from './modals/ClientViewModal';
import ClientCard from './clientManagement/ClientCard';
import ClientFormModal from './modals/ClientFormModal';

const ClientManagement: React.FC = () => {
  const { data: clients, isLoading, isError, error } = useClients();
  const deleteClientMutation = useDeleteClient();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'projects' | 'email'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Get unique tags from clients
  const availableTags = useMemo(() => {
    if (!clients) return [];
    const tags = new Set<string>();
    clients.forEach(client => {
      if (client.additionalInformation?.leadPersonName) tags.add('Has Lead Person');
      if (client.additionalInformation?.email) tags.add('Has Email');
      if (client.additionalInformation?.phone) tags.add('Has Phone');
      if (Array.isArray(client.project) && client.project.length > 0) tags.add('Has Projects');
      if (Array.isArray(client.project) && client.project.length === 0) tags.add('No Projects');
    });
    return Array.from(tags);
  }, [clients]);

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    if (!clients) return [];

    let filtered = clients.filter(client => {
      const matchesSearch = client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          client.additionalInformation?.leadPersonName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          client.additionalInformation?.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTags = selectedTags.length === 0 || selectedTags.every(tag => {
        switch (tag) {
          case 'Has Lead Person': return !!client.additionalInformation?.leadPersonName;
          case 'Has Email': return !!client.additionalInformation?.email;
          case 'Has Phone': return !!client.additionalInformation?.phone;
          case 'Has Projects': return Array.isArray(client.project) && client.project.length > 0;
          case 'No Projects': return Array.isArray(client.project) && client.project.length === 0;
          default: return true;
        }
      });

      return matchesSearch && matchesTags;
    });

    // Sort clients
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'name':
          aValue = a.clientName.toLowerCase();
          bValue = b.clientName.toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'projects':
          aValue = Array.isArray(a.project) ? a.project.length : 0;
          bValue = Array.isArray(b.project) ? b.project.length : 0;
          break;
        case 'email':
          aValue = a.additionalInformation?.email?.toLowerCase() || '';
          bValue = b.additionalInformation?.email?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [clients, searchTerm, sortBy, sortOrder, selectedTags]);

  // Analytics data
  const analytics = useMemo(() => {
    if (!clients) return null;

    const totalClients = clients.length;
    const clientsWithProjects = clients.filter(c => Array.isArray(c.project) && c.project.length > 0).length;
    const clientsWithContacts = clients.filter(c => c.additionalInformation?.email || c.additionalInformation?.phone).length;
    const totalProjects = clients.reduce((sum, c) => sum + (Array.isArray(c.project) ? c.project.length : 0), 0);
    const recentClients = clients.filter(c => {
      const created = new Date(c.createdAt);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return created >= thirtyDaysAgo;
    }).length;

    return {
      totalClients,
      clientsWithProjects,
      clientsWithContacts,
      totalProjects,
      recentClients,
      averageProjectsPerClient: totalClients > 0 ? (totalProjects / totalClients).toFixed(1) : '0',
    };
  }, [clients]);

  const removeTag = (tagToRemove: string) => {
    setSelectedTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const addTag = (tagToAdd: string) => {
    if (!selectedTags.includes(tagToAdd)) {
      setSelectedTags(prev => [...prev, tagToAdd]);
    }
  };

  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  // State for viewing client details
  const [viewingClient, setViewingClient] = useState<Client | null>(null);
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const [formData, setFormData] = useState<CreateClientData>({
    clientName: '',
    additionalInformation: {
      leadPersonName: '',
      phone: '',
      email: '',
      secondaryContact: '',
      websiteUrl: '',
    },
    project: [],
  });

  const openViewModal = (client: Client) => {
    setViewingClient(client);
    onViewOpen();
  };

  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);


  const handleDeleteClient = async () => {
    if (!deletingClient) return;

    try {
      await deleteClientMutation.mutateAsync(deletingClient._id);
      toast({
        title: 'Success',
        description: 'Client deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setDeletingClient(null);
      onDeleteClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete client',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openCreateModal = () => {
    setEditingClient(null);
    setFormData({
      clientName: '',
      additionalInformation: {
        leadPersonName: '',
        phone: '',
        email: '',
        secondaryContact: '',
        websiteUrl: '',
      },
      project: [],
    });
    onOpen();
  };

  const openEditModal = (client: Client) => {
    setEditingClient(client);
    // TypeScript: _id is not part of CreateClientData, but needed for edit. Use type assertion.
    setFormData({
      clientName: client.clientName,
      additionalInformation: {
        leadPersonName: client.additionalInformation?.leadPersonName || '',
        phone: client.additionalInformation?.phone || '',
        email: client.additionalInformation?.email || '',
        secondaryContact: client.additionalInformation?.secondaryContact || '',
        websiteUrl: client.additionalInformation?.websiteUrl || '',
      },
      project: Array.isArray(client.project)
        ? client.project.map((p: any) => typeof p === 'string' ? p : p._id)
        : [],
    } as CreateClientData);
    // Pass _id to ClientForm via editingClient, not formData
    onOpen();
  };

  const openDeleteDialog = (client: Client) => {
    setDeletingClient(client);
    onDeleteOpen();
  };

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Loading clients...</Text>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={8} textAlign="center">
        <Text color="red.500">Error loading clients: {error?.message || 'Unknown error'}</Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Analytics */}
      <VStack spacing={6} align="stretch" mb={8}>
        <Flex justify="space-between" align="center" mb={4}>
          
          <VStack align="start" spacing={2}>
                      
          <Heading size="lg" color="blue.700">Client Management</Heading>
                      <Text color="gray.600" fontSize="lg">
                        Manage your clients, track their projects, and maintain contact information.
                      </Text>
                    </VStack>
          <PermissionGuard permission={Permission.CREATE_CLIENTS}>
            <LimitRestrictedButton resource="clients" leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreateModal} size="lg">
              Add Client
            </LimitRestrictedButton>
          </PermissionGuard>
        </Flex>
        <Divider />

        {/* Analytics Cards */}
        {analytics && (
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }} gap={4}>
            <GridItem>
              <Stat bg="blue.50" p={4} borderRadius="lg" border="1px" borderColor="blue.200">
                <StatLabel color="blue.600">Total Clients</StatLabel>
                <StatNumber color="blue.700">{analytics.totalClients}</StatNumber>
                <StatHelpText color="blue.500">Active clients</StatHelpText>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat bg="green.50" p={4} borderRadius="lg" border="1px" borderColor="green.200">
                <StatLabel color="green.600">With Projects</StatLabel>
                <StatNumber color="green.700">{analytics.clientsWithProjects}</StatNumber>
                <StatHelpText color="green.500">{analytics.totalClients > 0 ? Math.round((analytics.clientsWithProjects / analytics.totalClients) * 100) : 0}% of clients</StatHelpText>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat bg="purple.50" p={4} borderRadius="lg" border="1px" borderColor="purple.200">
                <StatLabel color="purple.600">Total Projects</StatLabel>
                <StatNumber color="purple.700">{analytics.totalProjects}</StatNumber>
                <StatHelpText color="purple.500">Avg: {analytics.averageProjectsPerClient}</StatHelpText>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat bg="orange.50" p={4} borderRadius="lg" border="1px" borderColor="orange.200">
                <StatLabel color="orange.600">With Contacts</StatLabel>
                <StatNumber color="orange.700">{analytics.clientsWithContacts}</StatNumber>
                <StatHelpText color="orange.500">{analytics.totalClients > 0 ? Math.round((analytics.clientsWithContacts / analytics.totalClients) * 100) : 0}% contactable</StatHelpText>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat bg="teal.50" p={4} borderRadius="lg" border="1px" borderColor="teal.200">
                <StatLabel color="teal.600">Recent (30d)</StatLabel>
                <StatNumber color="teal.700">{analytics.recentClients}</StatNumber>
                <StatHelpText color="teal.500">New clients</StatHelpText>
              </Stat>
            </GridItem>
          </Grid>
        )}

        <Divider />

        {/* Search and Filter Controls */}
        <VStack spacing={4} align="stretch" bg={'white'} p={4} borderRadius="md" boxShadow="md">
          <HStack spacing={4} wrap="wrap">
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search clients by name, lead person, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="white"
                border="1px"
                borderColor="gray.300"
                _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px blue.400" }}
              />
            </InputGroup>
            
            <Select maxW="200px" value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} bg="white">
              <option value="name">Sort by Name</option>
              <option value="created">Sort by Created Date</option>
              <option value="projects">Sort by Project Count</option>
              <option value="email">Sort by Email</option>
            </Select>
            
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              leftIcon={<ChevronDownIcon transform={sortOrder === 'desc' ? 'rotate(180deg)' : 'none'} />}
            >
              {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            </Button>

            <Menu>
              <MenuButton as={Button} leftIcon={<SettingsIcon />} variant="outline">
                Filter ({selectedTags.length})
              </MenuButton>
              <MenuList>
                {availableTags.map(tag => (
                  <MenuItem
                    key={tag}
                    onClick={() => addTag(tag)}
                    bg={selectedTags.includes(tag) ? 'blue.50' : 'white'}
                    color={selectedTags.includes(tag) ? 'blue.700' : 'gray.700'}
                  >
                    {tag}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </HStack>

          {/* Active Filter Tags */}
          {selectedTags.length > 0 && (
            <Box>
              <Text fontSize="sm" color="gray.600" mb={2}>Active Filters:</Text>
              <Wrap spacing={2}>
                {selectedTags.map(tag => (
                  <WrapItem key={tag}>
                    <Tag size="md" colorScheme="blue" variant="solid">
                      <TagLabel>{tag}</TagLabel>
                      <TagCloseButton onClick={() => removeTag(tag)} />
                    </Tag>
                  </WrapItem>
                ))}
                <WrapItem>
                  <Button size="xs" variant="ghost" onClick={() => setSelectedTags([])}>
                    Clear All
                  </Button>
                </WrapItem>
              </Wrap>
            </Box>
          )}
        </VStack>
      </VStack>

      {/* Results Summary */}
      <Flex justify="space-between" align="center" mb={4}>
        <Text color="gray.600">
          Showing {filteredAndSortedClients.length} of {clients?.length || 0} clients
        </Text>
        {filteredAndSortedClients.length > 0 && (
          <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
            {filteredAndSortedClients.length} result{filteredAndSortedClients.length !== 1 ? 's' : ''}
          </Badge>
        )}
      </Flex>

      {/* Client Grid */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {filteredAndSortedClients.map(client => (
          <ClientCard
            key={client._id}
            client={client}
            onView={openViewModal}
            onEdit={openEditModal}
            onDelete={openDeleteDialog}
          />
        ))}
      </SimpleGrid>
      {/* View Client Details Modal */}
      <ClientViewModal isOpen={isViewOpen} onClose={onViewClose} client={viewingClient} />

      {/* Empty State */}
      {filteredAndSortedClients.length === 0 && clients && clients.length > 0 && (
        <Box textAlign="center" py={10}>
          <Text fontSize="lg" color="gray.500" mb={4}>
            No clients match your current search and filter criteria.
          </Text>
          <Button variant="outline" onClick={() => {
            setSearchTerm('');
            setSelectedTags([]);
          }}>
            Clear Filters
          </Button>
        </Box>
      )}

      {clients?.length === 0 && (
        <Box textAlign="center" py={10}>
          <Text fontSize="lg" color="gray.500" mb={4}>
            No clients found. Create your first client to get started!
          </Text>
          <PermissionGuard permission={Permission.CREATE_CLIENTS}>
            <LimitRestrictedButton resource="clients" leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreateModal}>
              Add Your First Client
            </LimitRestrictedButton>
          </PermissionGuard>
        </Box>
      )}

      {/* Create/Edit Modal */}
      <ClientFormModal
        isOpen={isOpen}
        onClose={onClose}
        formData={formData}
        onFormChange={setFormData}
        isEdit={!!editingClient}
        editingClient={editingClient}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Client
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete "{deletingClient?.clientName}"? This action cannot be
              undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteClient}
                ml={3}
                isLoading={deleteClientMutation.isPending}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ClientManagement;
